"use client";

import { useState, FormEvent } from "react";
import Link from "next/link";
import { ArrowRight, Check, AlertCircle, Loader2 } from "lucide-react";
import { Select } from "@/components/ui/select";
import { useT } from "@/i18n/provider";

export type LeadField = {
  name: string;
  label: string;
  type?: "text" | "email" | "url" | "textarea" | "select";
  required?: boolean;
  placeholder?: string;
  options?: string[];
  rows?: number;
};

type Props = {
  kind: "demo" | "contact";
  fields: LeadField[];
  submitLabel: string;
  successTitle: string;
  successBody: string;
};

export function LeadForm({
  kind,
  fields,
  submitLabel,
  successTitle,
  successBody,
}: Props) {
  const t = useT();
  const [status, setStatus] = useState<"idle" | "submitting" | "ok" | "error">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setError(null);

    const fd = new FormData(e.currentTarget);
    const payload: Record<string, string> = { kind };
    for (const [k, v] of fd.entries()) {
      if (typeof v === "string") payload[k] = v;
    }
    payload.source = typeof window !== "undefined" ? window.location.href : "";

    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as { ok: boolean; error?: string };
      if (!res.ok || !data.ok) {
        setStatus("error");
        setError(data.error || t("leadForm.genericError"));
        return;
      }
      setStatus("ok");
    } catch (err) {
      setStatus("error");
      setError(t("leadForm.networkError"));
    }
  }

  if (status === "ok") {
    return (
      <div className="rounded-2xl border border-status-low/30 bg-status-low/5 p-8 text-center">
        <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-status-low/15 text-status-low">
          <Check size={22} />
        </div>
        <h3 className="mt-4 text-2xl font-semibold text-fg-bright">
          {successTitle}
        </h3>
        <p className="mt-2 text-[14px] text-fg-dim">{successBody}</p>
        <Link
          href="/"
          className="mt-6 inline-flex items-center gap-1.5 text-[13px] text-brand-300 hover:text-brand-200"
        >
          {t("leadForm.backToHome")}
        </Link>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-5 rounded-2xl border border-line-subtle bg-bg-raised p-6 md:p-8"
    >
      {fields.map((f) => (
        <div key={f.name}>
          <label
            htmlFor={f.name}
            className="block text-[12px] font-medium text-fg-dim mb-1.5"
          >
            {f.label}
            {f.required && <span className="text-brand-300 ml-1">*</span>}
          </label>
          {f.type === "textarea" ? (
            <textarea
              id={f.name}
              name={f.name}
              required={f.required}
              rows={f.rows ?? 4}
              placeholder={f.placeholder}
              className="w-full rounded-lg border border-line bg-bg-elev px-3.5 py-2.5 text-[14px] text-fg placeholder:text-fg-muted focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 transition resize-y"
            />
          ) : f.type === "select" ? (
            <Select
              id={f.name}
              name={f.name}
              required={f.required}
              defaultValue=""
              className="w-full"
            >
              <option value="" disabled>
                {f.placeholder || t("leadForm.selectPlaceholder")}
              </option>
              {f.options?.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          ) : (
            <input
              id={f.name}
              name={f.name}
              type={f.type ?? "text"}
              required={f.required}
              placeholder={f.placeholder}
              className="w-full rounded-lg border border-line bg-bg-elev px-3.5 py-2.5 text-[14px] text-fg placeholder:text-fg-muted focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 transition"
            />
          )}
        </div>
      ))}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-status-critical/30 bg-status-critical/5 px-3.5 py-2.5 text-[13px] text-status-critical">
          <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-grad px-5 py-2.5 text-[13px] font-semibold text-white shadow-glow hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? (
          <>
            <Loader2 size={14} className="animate-spin" /> {t("leadForm.sending")}
          </>
        ) : (
          <>
            {submitLabel}
            <ArrowRight size={14} />
          </>
        )}
      </button>
    </form>
  );
}
